import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiCheck, FiX, FiMapPin, FiCalendar, FiUsers, FiDollarSign, FiCreditCard, FiPhone, FiUser, FiHome } from 'react-icons/fi';
import { formatarData } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import { FORMAS_PAGAMENTO } from '../constants/formasPagamento';

const Reservar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [viagem, setViagem] = useState(null);
  const [assentos, setAssentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reservando, setReservando] = useState(false);
  const [assentoSelecionado, setAssentoSelecionado] = useState(null);
  const [pisoVisivel, setPisoVisivel] = useState('inferior'); // inferior | superior
  const [formData, setFormData] = useState({
    nome: '',
    endereco: '',
    bairro: '',
    cidade: '',
    telefone: '',
    forma_pagamento: '',
    valor: '',
    observacoes: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [viagemRes, assentosRes] = await Promise.all([
        axios.get(`/api/viagens/public/${id}`),
        axios.get(`/api/assentos/public/viagem/${id}`)
      ]);

      setViagem(viagemRes.data);
      setAssentos(assentosRes.data);
      
    } catch (error) {
      toast.error('Erro ao carregar dados da viagem');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'disponivel':
        return 'bg-green-500 hover:bg-green-600 cursor-pointer';
      case 'reservado':
        return 'bg-yellow-500 cursor-not-allowed opacity-75';
      case 'ocupado':
        return 'bg-red-500 cursor-not-allowed opacity-75';
      default:
        return 'bg-gray-300';
    }
  };

  const handleAssentoClick = (assento) => {
    if (assento.status === 'disponivel') {
      setAssentoSelecionado(assento);
    }
  };

  const handleReservar = async () => {
    if (!assentoSelecionado) {
      toast.error('Selecione um assento');
      return;
    }

    if (!formData.nome || formData.nome.trim() === '') {
      toast.error('Informe seu nome');
      return;
    }

    if (!formData.telefone || formData.telefone.trim() === '') {
      toast.error('Informe seu telefone para contato');
      return;
    }

    setReservando(true);

    try {
      await axios.post('/api/pre-reservas/publica', {
        viagem_id: id,
        assento_id: assentoSelecionado.id,
        nome: formData.nome.trim(),
        endereco: formData.endereco.trim() || null,
        bairro: formData.bairro.trim() || null,
        cidade: formData.cidade.trim() || null,
        telefone: formData.telefone.trim(),
        forma_pagamento: formData.forma_pagamento || '',
        valor: formData.valor ? parseFloat(formData.valor) : null,
        observacoes: formData.observacoes || ''
      });

      navigate('/agradecimento');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao realizar pré-reserva');
    } finally {
      setReservando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (!viagem) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center bg-sky-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Viagem não encontrada</h2>
          <Link to="/" className="text-sky-600 hover:text-sky-700">
            Voltar para a página inicial
          </Link>
        </div>
      </div>
    );
  }

  if (viagem.lotado) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center bg-sky-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Viagem Lotada</h2>
          <p className="text-gray-600 mb-6">Esta viagem não possui mais poltronas disponíveis.</p>
          <Link to="/viagens" className="text-sky-600 hover:text-sky-700">
            Ver outras viagens disponíveis
          </Link>
        </div>
      </div>
    );
  }

  // Organizar assentos por fileira (igual ao mapa do admin - mostrando os dois lados)
  const assentosPorFileira = {};
  if (assentos && assentos.length > 0) {
    assentos.forEach(assento => {
      const key = assento.fileira;
      if (!assentosPorFileira[key]) {
        assentosPorFileira[key] = [];
      }
      assentosPorFileira[key].push(assento);
    });
  }

  const fileiras = Object.keys(assentosPorFileira).sort((a, b) => a.localeCompare(b));
  const isDoubleDeckerPisos = viagem?.tipo_onibus === 'double_decker_60' || viagem?.tipo_onibus === 'double_decker_56';
  const fileirasInferior = isDoubleDeckerPisos
    ? fileiras.filter((fileira) =>
        assentosPorFileira[fileira]?.some((a) => a.numero >= 45)
      )
    : fileiras;
  const fileirasSuperior = isDoubleDeckerPisos
    ? fileiras.filter((fileira) =>
        assentosPorFileira[fileira]?.some((a) => a.numero <= 44)
      )
    : [];
  const fileirasParaRender = isDoubleDeckerPisos
    ? (pisoVisivel === 'inferior' ? fileirasInferior : fileirasSuperior)
    : fileiras;

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/viagem/${id}`}
            className="inline-flex items-center text-white hover:text-sky-300 mb-4"
          >
            <FiArrowLeft className="mr-2" size={18} />
            Voltar para detalhes da viagem
          </Link>
          <h1 className="text-4xl font-bold text-white mb-2">Reservar Viagem</h1>
          <p className="text-white/70">{viagem.titulo}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações da Viagem */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dados da Viagem */}
            <div className="bg-sky-50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Informações da Viagem</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center text-gray-700">
                  <FiMapPin className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Destino:</span>
                  <span className="ml-2">{viagem.destino}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <FiCalendar className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Data:</span>
                  <span className="ml-2">{formatarData(viagem.data_ida)}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <FiUsers className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Poltronas:</span>
                  <span className="ml-2">{viagem.vagas_disponiveis || viagem.vagas || 0} disponíveis</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <FiDollarSign className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Valor:</span>
                  <span className="ml-2">R$ {parseFloat(viagem.valor || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Seleção de Assento */}
            <div className="bg-sky-50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Selecione seu Assento</h2>
              
              {/* Legenda */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-green-500"></div>
                  <span className="text-gray-700">Disponível</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-yellow-500"></div>
                  <span className="text-gray-700">Reservado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-red-500"></div>
                  <span className="text-gray-700">Ocupado</span>
                </div>
              </div>

              {/* Mapa de Assentos - layout igual ao admin (dois lados por fileira) */}
              <div className="space-y-4">
                {assentos.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">
                    Assentos ainda não foram criados para esta viagem. Entre em contato com o administrador.
                  </p>
                ) : (
                  <>
                    {isDoubleDeckerPisos && (
                      <div className="flex justify-center mb-4">
                        <div className="inline-flex rounded-full bg-sky-100 p-1">
                          <button
                            type="button"
                            onClick={() => setPisoVisivel('inferior')}
                            className={`px-4 py-1 text-xs font-semibold rounded-full transition-colors ${
                              pisoVisivel === 'inferior'
                                ? 'bg-white text-sky-900 shadow-sm'
                                : 'text-sky-800 hover:bg-sky-200'
                            }`}
                          >
                            Piso Inferior
                          </button>
                          <button
                            type="button"
                            onClick={() => setPisoVisivel('superior')}
                            className={`px-4 py-1 text-xs font-semibold rounded-full transition-colors ${
                              pisoVisivel === 'superior'
                                ? 'bg-white text-sky-900 shadow-sm'
                                : 'text-sky-800 hover:bg-sky-200'
                            }`}
                          >
                            Piso Superior
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-center mb-4">
                      <div className="text-center">
                        <div className="w-32 h-8 bg-gray-300 rounded mb-2 flex items-center justify-center text-sm font-bold">
                          ENTRADA
                        </div>
                      </div>
                    </div>

                    {/* Lista de fileiras para o piso selecionado (ou todos nos outros tipos) */}
                    {fileirasParaRender.map((fileira) => {
                      const assentosFileira = assentosPorFileira[fileira].sort((a, b) => a.numero - b.numero);
                      const ladoE = assentosFileira.filter(a => a.lado === 'E');
                      const ladoD = assentosFileira.filter(a => a.lado === 'D');

                      return (
                        <div key={fileira} className="flex items-center gap-4">
                          <div className="w-12 text-center font-bold text-gray-700">{fileira}</div>
                          <div className="flex-1 flex gap-2">
                            <div className="flex-1 flex gap-2 justify-end">
                              {ladoE.map((assento) => {
                                const isSelected = assentoSelecionado?.id === assento.id;
                                const isOcupado = assento.status === 'ocupado';
                                return (
                                  <button
                                    key={assento.id}
                                    onClick={() => handleAssentoClick(assento)}
                                    disabled={isOcupado || assento.status === 'reservado'}
                                    className={`
                                      w-12 h-12 rounded-lg text-white font-bold text-sm
                                      transition-all
                                      ${isSelected ? 'ring-4 ring-yellow-400 ring-offset-2' : ''}
                                      ${getStatusColor(assento.status)}
                                      ${isOcupado || assento.status === 'reservado' ? 'cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                    title={
                                      assento.status === 'ocupado' 
                                        ? `Poltrona ${assento.numero} - Ocupado`
                                        : assento.status === 'reservado'
                                        ? `Poltrona ${assento.numero} - Reservado`
                                        : `Poltrona ${assento.numero} - Disponível`
                                    }
                                  >
                                    {assento.numero}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="w-8"></div>
                            <div className="flex-1 flex gap-2">
                              {ladoD.map((assento) => {
                                const isSelected = assentoSelecionado?.id === assento.id;
                                const isOcupado = assento.status === 'ocupado';
                                return (
                                  <button
                                    key={assento.id}
                                    onClick={() => handleAssentoClick(assento)}
                                    disabled={isOcupado || assento.status === 'reservado'}
                                    className={`
                                      w-12 h-12 rounded-lg text-white font-bold text-sm
                                      transition-all
                                      ${isSelected ? 'ring-4 ring-yellow-400 ring-offset-2' : ''}
                                      ${getStatusColor(assento.status)}
                                      ${isOcupado || assento.status === 'reservado' ? 'cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                    title={
                                      assento.status === 'ocupado' 
                                        ? `Poltrona ${assento.numero} - Ocupado`
                                        : assento.status === 'reservado'
                                        ? `Poltrona ${assento.numero} - Reservado`
                                        : `Poltrona ${assento.numero} - Disponível`
                                    }
                                  >
                                    {assento.numero}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {assentoSelecionado && (
                <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg">
                  <p className="text-green-800 font-medium">
                    ✓ Poltrona {assentoSelecionado.numero} selecionada
                  </p>
                </div>
              )}
            </div>

            {/* Informações Adicionais */}
            <div className="bg-sky-50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Informações para Contato</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    placeholder="Seu nome completo"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone para Contato *
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        let formatted = value;
                        if (value.length <= 10) {
                          formatted = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
                        } else {
                          formatted = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                        }
                        setFormData({ ...formData, telefone: formatted });
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="(00) 00000-0000"
                      maxLength="15"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Usaremos este número para entrar em contato e finalizar sua reserva</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Forma de Pagamento Preferida
                  </label>
                  <select
                    value={formData.forma_pagamento}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                  >
                    <option value="">Selecione...</option>
                    {FORMAS_PAGAMENTO.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    rows="3"
                    placeholder="Informações adicionais, preferências, etc..."
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Atenção:</strong> Esta é uma pré-reserva. A administração entrará em contato para finalizar sua reserva e confirmar os detalhes de pagamento.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Resumo */}
          <div className="lg:col-span-1">
            <div className="bg-sky-50 rounded-lg p-6 border-2 border-sky-200 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Resumo da Reserva</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-700">
                  <span>Viagem:</span>
                  <span className="font-medium text-right">{viagem.titulo}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Destino:</span>
                  <span className="font-medium">{viagem.destino}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Data:</span>
                  <span className="font-medium">{formatarData(viagem.data_ida)}</span>
                </div>
                {assentoSelecionado && (
                  <div className="flex justify-between text-gray-700">
                    <span>Poltrona:</span>
                    <span className="font-medium">{assentoSelecionado.numero}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-700">
                  <span>Valor:</span>
                  <span className="font-bold text-sky-600">
                    R$ {parseFloat(viagem.valor || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={handleReservar}
                disabled={!assentoSelecionado || !formData.nome || !formData.telefone || reservando || viagem.lotado}
                className="w-full bg-yellow-400 text-black py-3 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reservando ? 'Enviando Pré-reserva...' : 'Solicitar Pré-reserva'}
              </button>

              {viagem.lotado && (
                <p className="mt-4 text-sm text-red-600 text-center font-medium">
                  Viagem lotada - Não há mais poltronas
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reservar;
